import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/FirebaseAuthContext';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import Profile from './pages/Profile';
import CreateListing from './pages/CreateListing';
import EditListing from './pages/EditListing';
import ItemDetail from './pages/ItemDetail';
import Checkout from './pages/Checkout';
import Transactions from './pages/Transactions';
import TransactionDetail from './pages/TransactionDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import SellerDashboard from './pages/SellerDashboard';
import MyOrders from './pages/MyOrders';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import SavedItems from './pages/SavedItems';
import Settings from './pages/Settings';
import PaymentSettings from './pages/PaymentSettings';
import ContactUs from './pages/ContactUs';
import AdminDashboard from './pages/AdminDashboard';
import Careers from './pages/Careers';
import Blog from './pages/Blog';
import HelpCenter from './pages/HelpCenter';
import Safety from './pages/Safety';
import SellerGuide from './pages/SellerGuide';
import BuyerGuide from './pages/BuyerGuide';
import MeasuringGuide from './pages/MeasuringGuide';
import CommunityGuidelines from './pages/CommunityGuidelines';
import CookiePolicy from './pages/CookiePolicy';
import About from './pages/About';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import BusinessApplication from './pages/BusinessApplication';
import BulkCreateListings from './pages/BulkCreateListings';
import BusinessRegister from './pages/BusinessRegister';
import BusinessApplicationSubmitted from './pages/BusinessApplicationSubmitted';

// Components
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import MobileBottomNav from './components/common/MobileBottomNav';
import ScrollToTop from './components/common/ScrollToTop';
import LoadingSpinner from './components/common/LoadingSpinner';

import './styles/App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return isAdmin ? children : <Navigate to="/" />;
};

function App() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <ScrollToTop />
      <div className="app">
        <Header />
        <main className="main-content">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/business-register" element={<BusinessRegister />} />
            <Route path="/business-application-submitted" element={<BusinessApplicationSubmitted />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/items/:id" element={<ItemDetail />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/seller-guide" element={<SellerGuide />} />
            <Route path="/buyer-guide" element={<BuyerGuide />} />
            <Route path="/measuring-guide" element={<MeasuringGuide />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/cookies" element={<CookiePolicy />} />

            {/* Protected Routes */}
            <Route
              path="/create-listing"
              element={
                <ProtectedRoute>
                  <CreateListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-listing/:id"
              element={
                <ProtectedRoute>
                  <EditListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/:itemId"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions/:id"
              element={
                <ProtectedRoute>
                  <TransactionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/seller-dashboard"
              element={
                <ProtectedRoute>
                  <SellerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-orders"
              element={
                <ProtectedRoute>
                  <MyOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:userId"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved-items"
              element={
                <ProtectedRoute>
                  <SavedItems />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-settings"
              element={
                <ProtectedRoute>
                  <PaymentSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/apply"
              element={
                <ProtectedRoute>
                  <BusinessApplication />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-create-listings"
              element={
                <ProtectedRoute>
                  <BulkCreateListings />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
        <Footer />
        <MobileBottomNav />
      </div>
    </Router>
  );
}

export default App;
